import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { Employee } from "@/types/db";
import { fmtDay, roleLabel } from "@/lib/utils";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";

export function Directory() {
  const [people, setPeople] = useState<Employee[]>([]);
  const [q, setQ] = useState("");
  const [dept, setDept] = useState<"all" | string>("all");
  const [open, setOpen] = useState<Employee | null>(null);

  useEffect(() => { void load(); }, []);
  async function load() {
    const { data } = await supabase.from("hq_directory")
      .select("id,first_name,last_name,email,phone,job_title,department,role,start_date,profile_picture_path")
      .order("first_name");
    setPeople((data as Employee[]) ?? []);
  }

  const depts = useMemo(
    () => Array.from(new Set(people.map(p => p.department).filter(Boolean))).sort() as string[],
    [people],
  );
  const filtered = useMemo(() => people.filter(p => {
    if (dept !== "all" && p.department !== dept) return false;
    if (q) {
      const hay = `${p.first_name ?? ""} ${p.last_name ?? ""} ${p.email} ${p.job_title ?? ""} ${p.department ?? ""}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  }), [people, q, dept]);

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">Team Directory</h1>

      <div className="flex flex-wrap gap-3">
        <input className="input max-w-sm" placeholder="Search name, title, department…" value={q} onChange={e => setQ(e.target.value)} />
        <select className="input max-w-[200px]" value={dept} onChange={e => setDept(e.target.value)}>
          <option value="all">All departments</option>
          {depts.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map(p => (
          <button key={p.id} onClick={() => setOpen(p)} className="card flex items-center gap-4 text-left transition hover:-translate-y-0.5 hover:shadow-soft hover:ring-teal">
            <EmployeeAvatar path={p.profile_picture_path} firstName={p.first_name} lastName={p.last_name} email={p.email} size={56} />
            <div className="min-w-0 flex-1">
              <div className="truncate font-semibold">{[p.first_name, p.last_name].filter(Boolean).join(" ") || p.email}</div>
              <div className="truncate text-sm text-gray2">{p.job_title ?? "—"}</div>
              <div className="truncate text-xs text-gray2">{p.department ?? roleLabel(p.role)}</div>
            </div>
          </button>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/60 p-4" onClick={() => setOpen(null)}>
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-soft" onClick={e => e.stopPropagation()}>
            <div className="mb-4 flex items-center gap-4">
              <EmployeeAvatar path={open.profile_picture_path} firstName={open.first_name} lastName={open.last_name} email={open.email} size={64} />
              <div>
                <h2 className="text-xl font-bold">{[open.first_name, open.last_name].filter(Boolean).join(" ") || open.email}</h2>
                <div className="text-sm text-gray2">{open.job_title ?? "—"}</div>
                <span className="pill pill-mint mt-1">{roleLabel(open.role)}</span>
              </div>
            </div>
            <dl className="space-y-2 text-sm">
              <Row label="Department" value={open.department} />
              <Row label="Email" value={<a href={`mailto:${open.email}`} className="text-teal-dark hover:underline">{open.email}</a>} />
              {open.phone && <Row label="Phone" value={<a href={`tel:${open.phone}`} className="text-teal-dark hover:underline">{open.phone}</a>} />}
              {open.start_date && <Row label="Started" value={fmtDay(open.start_date)} />}
            </dl>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3 border-b border-line/70 py-1.5">
      <dt className="text-gray2">{label}</dt>
      <dd className="text-right">{value || "—"}</dd>
    </div>
  );
}
